<template>
  <div class="rf-root">
    <appHeader></appHeader>

    <transition appear name="fade">
      <div class="rf-layout container">

        <!-- ── Sidebar ── -->
        <RecognitionSidebar />

        <!-- ── Main Feed ── -->
        <main class="rf-main">

          <div class="rf-feed-header">
            <div>
              <h1 class="rf-feed-title">Recognition Feed</h1>
              <p class="rf-feed-sub">Celebrating birthdays, work anniversaries and achievements across our company.</p>
            </div>
            <!-- Desktop: Give Recognition + filter on the right -->
            <div class="rf-feed-actions rf-feed-actions--desktop">
              <a href="javascript:void(0)" @click="$router.push({ path: '/recognition/give'})" class="rf-give-btn">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15">
                  <circle cx="12" cy="8" r="6"/>
                  <path d="M8.21 13.89L7 22l5-3 5 3-1.21-8.11"/>
                </svg>
                Give Recognition
              </a>
              <select class="rf-select" v-model="selectedFilter">
                <option value="all">All Activities</option>
                <option value="birthday">Birthdays</option>
                <option value="anniversary">Work Anniversaries</option>
                <option value="peer">Peer Recognition</option>
              </select>
            </div>
          </div>

          <!-- ── Mobile-only: This Month Summary strip ── -->
          <div class="rf-mobile-summary">
            <div class="rf-mobile-summary-row" v-for="stat in monthlySummary" :key="stat.label">
              <span class="rf-mobile-summary-label">{{ stat.label }}</span>
              <span class="rf-mobile-summary-val" :class="{ 'rf-mobile-summary-val--green': stat.positive }">{{ stat.value }}</span>
            </div>
          </div>

          <!-- ── Mobile-only: Give Recognition + filter in one row, below summary ── -->
          <div class="rf-feed-actions rf-feed-actions--mobile">
            <a href="javascript:void(0)" @click="$router.push({ path: '/recognition/give'})" class="rf-give-btn">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
              Give Recognition
            </a>
            <select class="rf-select" v-model="selectedFilter">
              <option value="all">All Activities</option>
              <option value="birthday">Birthdays</option>
              <option value="anniversary">Work Anniversaries</option>
              <option value="peer">Peer Recognition</option>
            </select>
          </div>

          <!-- ── Loading skeleton ── -->
          <template v-if="isLoading">
            <div v-for="n in 4" :key="n" class="rf-card rf-card--skeleton">
              <div class="rf-skeleton-icon"></div>
              <div class="rf-skeleton-body">
                <div class="rf-skeleton-line rf-skeleton-line--short"></div>
                <div class="rf-skeleton-line rf-skeleton-line--long"></div>
                <div class="rf-skeleton-line rf-skeleton-line--medium"></div>
              </div>
            </div>
          </template>

          <!-- ── Error state ── -->
          <div v-else-if="feedError" class="rf-feed-error">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" width="36" height="36">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <p>{{ feedError }}</p>
            <button class="rf-retry-btn" @click="loadFeed">Try again</button>
          </div>

          <!-- ── Empty state ── -->
          <div v-else-if="!isLoading && feedItems.length === 0" class="rf-feed-empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" width="40" height="40">
              <circle cx="12" cy="12" r="10"/>
              <path d="M8 15s1.5-2 4-2 4 2 4 2"/>
              <line x1="9" y1="9" x2="9.01" y2="9"/>
              <line x1="15" y1="9" x2="15.01" y2="9"/>
            </svg>
            <p>No activities found.</p>
          </div>

          <!-- ── Feed Cards ── -->
          <template v-else>
            <div v-for="item in filteredFeed" :key="item.id" class="rf-card">

              <!-- Card icon (left) -->
              <div class="rf-card-icon" :class="`rf-card-icon--${item.type}`">
                <svg v-if="item.type === 'birthday'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="20" height="20">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                <svg v-else-if="item.type === 'anniversary'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="20" height="20">
                  <rect x="3" y="4" width="18" height="18" rx="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="20" height="20">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
              </div>

              <!-- Card body -->
              <div class="rf-card-body">

                <!-- Tag + points + kudos badge -->
                <div class="rf-card-meta">
                  <span class="rf-tag" :class="`rf-tag--${item.type}`">
                    <span class="rf-tag-icon" v-html="item.icon"></span>
                    {{ item.tagLabel }}
                  </span>
                  <span class="rf-pts">{{ item.points }}</span>
                  <span v-if="item.is_kudos" class="rf-kudos-badge">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="11" height="11">
                      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/>
                      <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
                    </svg>
                    Kudos
                  </span>
                </div>

                <!-- Time -->
                <span class="rf-time">{{ item.time }}</span>

                <!-- ── Birthday / Anniversary — celebration card UX ── -->
                <template v-if="item.type === 'birthday' || item.type === 'anniversary'">
                  <div class="rf-celebration-card" :class="`rf-celebration-card--${item.type}`">

                    <!-- Colored header banner -->
                    <div class="rf-celebration-header">
                      <span class="rf-celebration-emoji">
                        <!-- Birthday -->
                        <svg
                          v-if="item.type === 'birthday'"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#000"
                          stroke-width="1.8"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          width="20"
                          height="20"
                        >
                          <path d="M4 12h16v8H4z" />
                          <path d="M12 4v4" />
                          <path d="M8 6c0 1-1 1.5-1 2.5S8 10 8 11" />
                          <path d="M12 6c0 1-1 1.5-1 2.5S12 10 12 11" />
                          <path d="M16 6c0 1-1 1.5-1 2.5S16 10 16 11" />
                        </svg>

                        <!-- Trophy -->
                        <svg
                          v-else
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#000"
                          stroke-width="1.8"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          width="20"
                          height="20"
                        >
                          <path d="M8 21h8" />
                          <path d="M12 17v4" />
                          <path d="M7 4h10v4a5 5 0 0 1-10 0V4z" />
                          <path d="M17 6h2a2 2 0 0 1 0 4h-2" />
                          <path d="M7 6H5a2 2 0 0 0 0 4h2" />
                        </svg>

                      </span>
                      <span class="rf-celebration-title">
                        {{ item.type === 'birthday' ? 'Happy Birthday!' : 'Work Anniversary!' }}
                      </span>
                    </div>

                    <!-- People celebrating -->
                    <div class="rf-celebration-people">
                      <div
                        v-for="(r, i) in (item.recipients && item.recipients.length ? item.recipients : (item.sender ? [item.sender] : []))"
                        :key="i"
                        class="rf-celebration-person"
                      >
                        <img :src="r.avatar" :alt="r.name" class="rf-celebration-avatar" />
                        <span class="rf-celebration-name">{{ r.name }}</span>
                      </div>
                    </div>

                    <!-- Warm message -->
                    <p
                      v-if="item.type === 'birthday' || item.type === 'anniversary'"
                      class="rf-celebration-message"
                      v-html="item.type === 'birthday'
                        ? 'Happy Birthday to our amazing team members! Wishing you a fantastic day filled with happiness, success, and great memories. Thank you for your hard work and dedication to the team. Have a wonderful year ahead!'
                        : 'Happy Work Anniversary to our amazing team members! Thank you for your dedication, hard work, and continued commitment. We truly appreciate everything you do and look forward to many more successful years together!'"
                    ></p>

                  </div>
                </template>

                <!-- ── Peer recognition — normal message ── -->
                <template v-else>
                  <p class="rf-card-text" v-html="item.message"></p>

                  <!-- Recipients strip -->
                  <div v-if="item.recipients && item.recipients.length > 0" class="rf-recipients">
                    <img
                      v-for="(r, i) in item.recipients"
                      :key="i"
                      :src="r.avatar"
                      :alt="r.name"
                      :title="r.name"
                      class="rf-recipient-avatar"
                    />
                    <span class="rf-recipients-label">
                      {{ item.recipients.length === 1 ? item.recipients[0].name : item.recipients.length + ' people recognized' }}
                    </span>
                  </div>
                </template>

                <!-- Quote — peer recognition personal message -->
                <blockquote v-if="item.quote" class="rf-quote">{{ item.quote }}</blockquote>

                <!-- Footer: avatars + action button -->
                <div class="rf-card-footer">
                  <div class="rf-footer-left">
                    <div class="rf-avatars">
                      <img
                        v-for="(avatar, i) in item.avatars"
                        :key="i"
                        class="rf-avatar"
                        :src="avatar"
                        alt="Colleague"
                      />
                      <span v-if="item.extraCount" class="rf-avatar rf-avatar--more">+{{ item.extraCount }}</span>
                    </div>
                    <span v-if="item.reactionLabel" class="rf-footer-text">{{ item.reactionLabel }}</span>
                  </div>
                  <button class="rf-action-btn" :class="`rf-action-btn--${item.type}`">
                    {{ item.actionLabel }}
                  </button>
                </div>

              </div>
            </div>
          </template>

          <!-- ── Load more ── -->
          <div class="rf-load-more" v-if="!isLoading && feedItems.length > 0">
            <span class="rf-showing">Showing {{ feedItems.length }} of {{ totalItems }} activities</span>
            <button class="rf-load-btn" v-if="hasMore" @click="loadMore" :disabled="isLoadingMore">
              {{ isLoadingMore ? 'Loading...' : 'Load more' }}
              <svg v-if="!isLoadingMore" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="13" height="13">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
          </div>

        </main>

        <!-- ── Right panel ── -->
        <aside class="rf-panel">
          <div class="rf-panel-sticky">

            <div class="rf-widget">
              <p class="rf-widget-label">My Recognition Balance</p>
              <div class="rf-balance-row">
                <span class="rf-balance-num">{{ balance.points.toLocaleString() }}</span>
                <span class="rf-balance-unit">pts</span>
              </div>
              <p class="rf-balance-sub">Recognitions Points</p>
              <button class="rf-store-btn" @click="$router.push({ path: '/shop/category/1'})">Start Shopping</button>
            </div>

            <div class="rf-widget">
              <p class="rf-widget-title">This Month's Summary</p>
              <div class="rf-row" v-for="stat in monthlySummary" :key="stat.label">
                <span class="rf-row-label">{{ stat.label }}</span>
                <span class="rf-row-val" :class="{ 'rf-row-val--green': stat.positive }">{{ stat.value }}</span>
              </div>
            </div>

            <div class="rf-widget">
              <p class="rf-widget-title">Quick Actions</p>
              <a v-for="action in quickActions" :key="action.label" :href="action.href" class="rf-quick-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="15" height="15">
                  <path :d="action.iconPath"/>
                </svg>
                {{ action.label }}
              </a>
            </div>

          </div>
        </aside>

      </div>
    </transition>

    <appFooter class="rf-footer-desktop"></appFooter>
    <navigationFooter class="rf-footer-mobile"></navigationFooter>
  </div>
</template>
<script>
// ── Replace entire <script> block in RecognitionFeed.vue ──

import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import navigationFooter from "@/components/navigationFooter.vue";
import RecognitionSidebar from "./RecognitionSidebar.vue";
import RecognitionService from "@/services/RecognitionService";

export default {
  name: "RecognitionFeed",

  components: {
    appHeader: Header,
    appFooter: Footer,
    navigationFooter,
    RecognitionSidebar,
  },

  data() {
    return {
      // ── Filter ───────────────────────────────────────
      selectedFilter: "all",

      // ── Feed data ─────────────────────────────────────
      feedItems: [],

      // ── Loading / error ───────────────────────────────
      isLoading:  false,
      feedError:  null,

      // ── Pagination ────────────────────────────────────
      currentPage: 1,
      totalPages:  1,
      totalItems:  0,
      isLoadingMore: false,

      // ── Right panel — quota ───────────────────────────
      balance: { points: 0 },

      // ── Right panel — monthly summary ─────────────────
      // Loaded from quota API
      monthlySummary: [
        { label: "Recognitions Balance", value: "0",  positive: true  },
        { label: "Recognitions Received", value: "0", positive: false },
        { label: "Recognitions Given",    value: "0", positive: false },
      ],

      quickActions: [
        {
          label:    "Give Recognition",
          href:     "/recognition/give",
          iconPath: "M12 2a6 6 0 1 1 0 12a6 6 0 0 1 0-12z M8.21 13.89L7 22l5-3 5 3-1.21-8.11",
        },
        {
          label:    "View My History",
          href:     "/recognition/history",
          iconPath: "M3 12a9 9 0 1 0 3-6.7 M3 3v6h6 M12 7v5l3 3",
        },
      ],

      // Icon map — used to assign icons after API response
      // since API doesn't return icon SVGs
      iconMap: {
        peer: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="13" height="13"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`,
        birthday: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="13" height="13"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/><path d="M12 2v2M9 4l1.5 1.5M15 4l-1.5 1.5"/></svg>`,
        anniversary: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="13" height="13"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`,
      },
    };
  },

  computed: {
    // Map frontend filter value to API event_type value
    apiTypeFilter() {
      const map = {
        all:         "all",
        birthday:    "Birthday",
        anniversary: "WorkAnniversary",
        peer:        "PeerRecognition",
      };
      return map[this.selectedFilter] ?? "all";
    },

    filteredFeed() {
      // Filtering is done server-side via API
      // This computed just returns feedItems as-is
      return this.feedItems;
    },

    hasMore() {
      return this.currentPage < this.totalPages;
    },
  },

  watch: {
    selectedFilter() {
      // Reset and reload when filter changes
      this.feedItems   = [];
      this.currentPage = 1;
      this.totalPages  = 1;
      this.loadFeed();
    },
  },

  methods: {
    // ── Load feed (first page or filter change) ───────────
    async loadFeed() {
      this.isLoading = true;
      this.feedError = null;

      try {
        const resp = await RecognitionService.getFeed({
          type:  this.apiTypeFilter,
          page:  1,
          limit: 20,
        });

        const { data, meta } = resp.data;

        // Enrich items with icon from iconMap
        this.feedItems   = this.enrichItems(data ?? []);
        this.currentPage = meta?.page        ?? 1;
        this.totalPages  = meta?.total_pages ?? 1;
        this.totalItems  = meta?.total       ?? 0;

      } catch (err) {
        this.feedError = err.response?.data?.message
          || "Failed to load recognition feed.";
      } finally {
        this.isLoading = false;
      }
    },

    // ── Load more (append next page) ──────────────────────
    async loadMore() {
      if (!this.hasMore || this.isLoadingMore) return;

      this.isLoadingMore = true;

      try {
        const resp = await RecognitionService.getFeed({
          type:  this.apiTypeFilter,
          page:  this.currentPage + 1,
          limit: 20,
        });

        const { data, meta } = resp.data;

        // Append to existing items
        this.feedItems   = [...this.feedItems, ...this.enrichItems(data ?? [])];
        this.currentPage = meta?.page        ?? this.currentPage + 1;
        this.totalPages  = meta?.total_pages ?? this.totalPages;
        this.totalItems  = meta?.total       ?? this.totalItems;

      } catch (err) {
        console.error("Failed to load more:", err);
      } finally {
        this.isLoadingMore = false;
      }
    },

    // ── Load quota for right panel balance ────────────────
    async loadQuota() {
      try {
        const resp = await RecognitionService.getMyQuota();
        const data = resp.data.data ?? {};

        this.balance = { points: data.available_points ?? 0 };

        // Update monthly summary with real quota data
        this.monthlySummary = [
          { label: "Recognitions Balance",  value: data.available_points?.toString() ?? "0", positive: true  },
          { label: "Recognitions Received", value: data.received_count?.toString()   ?? "0", positive: false },
          { label: "Recognitions Given",    value: data.used_points?.toString()       ?? "0", positive: false },
        ];

      } catch (err) {
        console.error("Failed to load quota:", err);
      }
    },

    // ── Enrich API items with frontend-only fields ────────
    // API returns type/tagLabel but not icon SVGs
    enrichItems(items) {
      return items.map((item) => ({
        ...item,
        icon: this.iconMap[item.type] ?? this.iconMap.peer,
      }));
    },
  },

  created() {
    this.loadFeed();
    this.loadQuota();
  },
};
</script>

<style lang="scss" scoped>

// ─────────────────────────────────────────────────────────
// Root
// ─────────────────────────────────────────────────────────
.rf-root {
  background: #fff;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

// ─────────────────────────────────────────────────────────
// Footer visibility
// Desktop footer shown by default; mobile footer hidden.
// Reversed at ≤768 px.
// ─────────────────────────────────────────────────────────
.rf-footer-desktop { display: block; }

// On desktop, hide the mobile nav footer without using display:none
// (which can break position:fixed children in some browsers).
// We suppress it visually and remove it from interaction.
.rf-footer-mobile {
  visibility: hidden;
  pointer-events: none;
  height: 0;
  overflow: hidden;
}

// ─────────────────────────────────────────────────────────
// 3-column grid (desktop)
// ─────────────────────────────────────────────────────────
.rf-layout {
  display: grid;
  grid-template-columns: 220px 1fr 280px;
  align-items: stretch;
  padding-top: 2rem;
  padding-bottom: 3rem;
  flex: 1;
}

// ─────────────────────────────────────────────────────────
// Feed column
// ─────────────────────────────────────────────────────────
.rf-main {
  padding: 0 2rem;
  min-width: 0;
}

// ─────────────────────────────────────────────────────────
// Feed header
// ─────────────────────────────────────────────────────────
.rf-feed-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;

  // Left side (title + sub) takes remaining space
  > div:first-child { flex: 1; }
}

.rf-feed-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 4px;
  line-height: 1.2;
}

.rf-feed-title-underline {
  width: 32px;
  height: 3px;
  background: $primary;
  border-radius: 2px;
  margin-bottom: 6px;
}

.rf-feed-sub {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.rf-select {
  flex-shrink: 0;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 7px 30px 7px 12px;
  font-size: 0.85rem;
  color: #374151;
  background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%236b7280'/%3E%3C/svg%3E") no-repeat right 10px center;
  appearance: none;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba($primary, 0.12);
  }
}

// ─────────────────────────────────────────────────────────
// Feed header actions row (Give Recognition btn + filter)
// ─────────────────────────────────────────────────────────
.rf-feed-actions {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: nowrap;

  // Desktop version: right-aligned in the header
  &--desktop {
    align-self: flex-start;
    margin-left: auto;
    display: flex;   // shown on desktop
  }

  // Mobile version: full-width row below summary, hidden on desktop
  &--mobile {
    display: none;
    width: 100%;
    margin-bottom: 0.875rem;
  }
}

// Give Recognition — solid primary button
.rf-give-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: $primary;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease;

  svg { stroke: #fff; flex-shrink: 0; }

  &:hover {
    background: darken($primary, 8%);
    text-decoration: none;
    color: #fff;
  }
}

// ─────────────────────────────────────────────────────────
// Mobile-only: This Month Summary strip
// Shown below the header on mobile, hidden on desktop
// (desktop sees it in the right panel widget)
// ─────────────────────────────────────────────────────────
.rf-mobile-summary {
  display: none;   // hidden on desktop by default
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0.875rem 1rem;
  margin-bottom: 0.875rem;
}

.rf-mobile-summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.85rem;

  &:last-child { border-bottom: none; }
}

.rf-mobile-summary-label { color: #4b5563; }

.rf-mobile-summary-val {
  font-weight: 600;
  color: #111827;

  &--green { color: #16a34a; }
}
// ── Birthday recipient banner ─────────────────────────────
// After — add these:
.rf-recipient-banner {
  border-radius: 8px;
  padding: 0.875rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 10px;

  &--birthday {
    background: linear-gradient(135deg, #fff1f2 0%, #fef3c7 100%);
    border: 1px solid #fecdd3;
  }

  &--anniversary {
    background: linear-gradient(135deg, #fffbeb 0%, #fef9c3 100%);
    border: 1px solid #fde68a;
  }
}

.rf-recipient-banner-top {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.rf-recipient-emoji {
  font-size: 1.75rem;
  line-height: 1;
  flex-shrink: 0;
}

.rf-recipient-heading {
  font-size: 0.9rem;
  font-weight: 700;
  margin: 0 0 3px;

  .rf-recipient-banner--birthday & { color: #be123c; }
  .rf-recipient-banner--anniversary & { color: #92400e; }
}

.rf-recipient-sub {
  font-size: 0.82rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.5;
}

.rf-recipient-reward {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 0.8rem;
  color: #15803d;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  padding: 5px 10px;
  line-height: 1.5;
  flex-wrap: wrap;
  justify-content: center;

  svg { stroke: #16a34a; flex-shrink: 0; margin-top: 2px; }
  strong { font-weight: 700; }
}
// ─────────────────────────────────────────────────────────
// Cards
// ─────────────────────────────────────────────────────────
.rf-card {
  display: flex;
  gap: 0.875rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 1rem 1.1rem;
  margin-bottom: 0.75rem;
  transition: box-shadow 0.15s ease;

  &:hover { box-shadow: 0 2px 10px rgba(0,0,0,0.07); }
}

.rf-card-icon {
  display: flex;
  align-items: flex-start;
  padding-top: 2px;
  flex-shrink: 0;

  > svg {
    padding: 8px;
    border-radius: 9px;
    box-sizing: content-box;
  }

  &--birthday   > svg { background: #fff1f2; stroke: #f43f5e; }
  &--anniversary > svg { background: #fffbeb; stroke: #f59e0b; }
  &--peer       > svg { background: #eff6ff; stroke: $primary; }
}

.rf-card-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

// Meta: tag pill + pts badge — side by side
.rf-card-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.rf-tag {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;

  &--birthday   { background: #fff1f2; color: #e11d48; }
  &--anniversary { background: #fffbeb; color: #d97706; }
  &--peer       { background: #eff6ff; color: $primary; }
}

// Points — blue pill, always visible
.rf-pts {
  font-size: 0.78rem;
  font-weight: 700;
  color: $primary;
  background: #eff6ff;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
}

// Time — sits on its own line so it never gets cut off on narrow screens
.rf-time {
  font-size: 0.78rem;
  color: #9ca3af;
  display: block;
  margin-top: -2px;
}

.rf-card-text {
  font-size: 0.875rem;
  color: #374151;
  line-height: 1.6;
  margin: 0;

  ::v-deep strong {
    color: #111827;
    font-weight: 600;
  }
}


.rf-quote {
  font-size: 0.85rem;
  color: #4b5563;
  font-style: italic;
  line-height: 1.6;
  border-left: 3px solid #bfdbfe;
  padding-left: 12px;
  margin: 0;
}

// Footer row: avatars + count on left, button on right
.rf-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 2px;
}

.rf-footer-left {
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
}

.rf-avatars {
  display: flex;
  align-items: center;
}

.rf-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #fff;
  margin-left: -6px;
  object-fit: cover;

  &:first-child { margin-left: 0; }

  &--more {
    background: #e5e7eb;
    color: #6b7280;
    font-size: 0.62rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    border-radius: 50%;
    border: 2px solid #fff;
    margin-left: -6px;
  }
}

.rf-footer-text {
  font-size: 0.78rem;
  color: #6b7280;
}

.rf-action-btn {
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 5px 16px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s ease;

  &--birthday {
    background: #fff1f2; color: #e11d48; border-color: #fecdd3;
    &:hover { background: #ffe4e6; }
  }
  &--anniversary {
    background: #fffbeb; color: #b45309; border-color: #fde68a;
    &:hover { background: #fef3c7; }
  }
  &--peer {
    background: #eff6ff; color: $primary; border-color: #bfdbfe;
    &:hover { background: #dbeafe; }
  }
}

// ─────────────────────────────────────────────────────────
// Load more
// ─────────────────────────────────────────────────────────
.rf-load-more {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0 0;
  flex-wrap: wrap;
  gap: 8px;
}

.rf-showing { font-size: 0.82rem; color: #9ca3af; }

.rf-load-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  font-size: 0.875rem;
  font-weight: 600;
  color: $primary;
  cursor: pointer;
  padding: 0;

  &:hover { text-decoration: underline; }
}

// ─────────────────────────────────────────────────────────
// Right panel — sticky on desktop
// ─────────────────────────────────────────────────────────
.rf-panel {
  padding-left: 1.5rem;
  border-left: 1px solid #e5e7eb;
}

.rf-panel-sticky {
  position: sticky;
  top: 80px;
}

.rf-widget {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 1.1rem 1.2rem;
  margin-bottom: 0.875rem;

  &:last-child { margin-bottom: 0; }
}

.rf-widget-label {
  font-size: 0.78rem;
  font-weight: 500;
  color: #6b7280;
  margin: 0 0 6px;
}

.rf-balance-row {
  display: flex;
  align-items: baseline;
  gap: 5px;
  margin-bottom: 2px;
}

.rf-balance-num {
  font-size: 2.1rem;
  font-weight: 700;
  color: $primary;
  line-height: 1;
}

.rf-balance-unit {
  font-size: 0.95rem;
  color: #9ca3af;
}

.rf-balance-sub {
  font-size: 0.75rem;
  color: #9ca3af;
  margin: 0 0 1rem;
}

.rf-store-btn {
  width: 100%;
  background: $primary;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 10px 0;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover { background: darken($primary, 8%); }
}

.rf-widget-title {
  font-size: 0.72rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 10px;
}

.rf-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 7px 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child { border-bottom: none; }
}

.rf-row-label { font-size: 0.85rem; color: #4b5563; }

.rf-row-val {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;

  &--green { color: #16a34a; }
}

.rf-quick-item {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 0.875rem;
  color: #374151;
  text-decoration: none;
  padding: 8px 4px;
  border-radius: 6px;
  transition: color 0.12s, background 0.12s;
  border-bottom: 1px solid #f3f4f6;

  &:last-child { border-bottom: none; }
  svg { stroke: #6b7280; flex-shrink: 0; }

  &:hover {
    color: $primary;
    background: #eff6ff;
    text-decoration: none;
    svg { stroke: $primary; }
  }
}

// ─────────────────────────────────────────────────────────
// Tablet (≤1100 px) — sidebar + feed / panel below
// ─────────────────────────────────────────────────────────
@media (max-width: 1100px) {
  .rf-layout {
    grid-template-columns: 210px 1fr;
    grid-template-areas:
      "sidebar main"
      "panel   panel";
    align-items: start;
  }

  .rf-panel {
    grid-area: panel;
    border-left: none;
    border-top: 1px solid #e5e7eb;
    padding-left: 0;
    padding-top: 1.25rem;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 0.875rem;
  }

  .rf-panel-sticky {
    position: static;
    display: contents;
  }

  .rf-widget { margin-bottom: 0; }
}

// ─────────────────────────────────────────────────────────
// Mobile (≤768 px)
// - Single column, no 3-col grid
// - Right panel (widgets) hidden — not useful on small screen
// - navigationFooter shown, appFooter hidden
// - Extra bottom padding so last card isn't hidden under nav footer
// ─────────────────────────────────────────────────────────
@media (max-width: 768px) {

  .rf-recipient-banner {
    padding: 0.75rem;
    gap: 8px;
  }

  .rf-recipient-emoji {
    font-size: 1.4rem;
  }

  .rf-recipient-heading {
    font-size: 0.82rem;
  }

  .rf-recipient-sub {
    font-size: 0.78rem;
  }

  .rf-recipient-reward {
    font-size: 0.75rem;
    padding: 4px 8px;
  }
  // Switch footers
  .rf-footer-desktop { display: none !important; }
  .rf-footer-mobile  {
    visibility: visible !important;
    pointer-events: auto !important;
    height: auto !important;
    overflow: visible !important;
  }

  .rf-layout {
    display: block;               // drop grid entirely on mobile
    padding: 0;
    margin: 0;
  }

  // Sidebar becomes a horizontal tab strip (handled inside RecognitionSidebar)
  // Main feed: full width, comfortable side padding, extra bottom so
  // content isn't hidden under the fixed navigationFooter
  .rf-main {
    padding: 1rem 1rem 90px; // 90px ≈ typical mobile nav footer height
    min-width: 0;
  }

  // Right panel completely hidden on mobile
  .rf-panel { display: none; }

  .rf-feed-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  .rf-feed-title   { font-size: 1.25rem; }
  .rf-feed-sub     { font-size: 0.82rem; }

  // Hide desktop actions row (it's inside the header on desktop)
  .rf-feed-actions--desktop { display: none !important; }

  // Show mobile actions row (below summary), as a single flex row
  .rf-feed-actions--mobile {
    display: flex !important;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    width: 100%;

    // Give Recognition takes 55% width
    .rf-give-btn {
      flex: 0 0 auto;
      white-space: nowrap;
      justify-content: center;
    }

    // Filter select fills remaining space
    .rf-select {
      flex: 1;
      width: auto;
      min-width: 0;
    }
  }

  // Show the monthly summary strip on mobile
  .rf-mobile-summary { display: block; }

  .rf-card {
    padding: 0.875rem;
    gap: 0.75rem;
    margin-bottom: 0.625rem;
    border-radius: 8px;
  }

  // On mobile: action button full-width below the avatars
  .rf-card-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .rf-action-btn {
    width: 100%;
    text-align: center;
    justify-content: center;
  }
}

// ─────────────────────────────────────────────────────────
// Small phones (≤420 px) — tighten further
// ─────────────────────────────────────────────────────────
@media (max-width: 420px) {
  .rf-main { padding: 0.75rem 0.75rem 90px; }

  .rf-card { padding: 0.75rem; gap: 0.625rem; }

  .rf-card-icon > svg { padding: 6px; }

  .rf-feed-title { font-size: 1.15rem; }
}

h1:after, h2:after, h3:after, h4:after, h5:after {
    display: unset;
    border: unset;
}

// ── Multi-recipient strip (peer recognition) ─────────────
.rf-recipients {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f5f3ff;
  border: 1px solid #e9d5ff;
  border-radius: 6px;
  padding: 6px 10px;
}

.rf-recipient-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #fff;
  margin-left: -6px;
  object-fit: cover;

  &:first-child { margin-left: 0; }
}

.rf-recipients-label {
  font-size: 0.78rem;
  font-weight: 500;
  color: #7c3aed;
  margin-left: 4px;
}

.rf-tag-icon {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;

  svg { stroke: currentColor; }
}

.rf-card--skeleton { pointer-events: none; }

.rf-skeleton-icon {
  width: 36px; height: 36px;
  border-radius: 9px;
  background: #e5e7eb;
  flex-shrink: 0;
  animation: rf-shimmer 1.4s infinite;
}

.rf-skeleton-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 4px;
}

.rf-skeleton-line {
  height: 10px;
  border-radius: 4px;
  background: #e5e7eb;
  animation: rf-shimmer 1.4s infinite;
  &--short  { width: 30%; }
  &--medium { width: 55%; }
  &--long   { width: 85%; }
}

@keyframes rf-shimmer {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
}

.rf-feed-error, .rf-feed-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 3rem 1rem;
  text-align: center;
  color: #9ca3af;
  p { font-size: 0.875rem; margin: 0; }
  svg { stroke: #d1d5db; }
}

.rf-retry-btn {
  background: none;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 16px;
  font-size: 0.82rem;
  font-weight: 500;
  color: $primary;
  cursor: pointer;
  &:hover { background: #eff6ff; }
}

// ── Birthday / Anniversary celebration card ───────────────
.rf-celebration-card {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.rf-celebration-card--birthday {
  .rf-celebration-header {
    background: linear-gradient(135deg, #f43f5e 0%, #fb923c 100%);
  }
}

.rf-celebration-card--anniversary {
  .rf-celebration-header {
    background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
  }
}

.rf-celebration-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
}

.rf-celebration-emoji {
  font-size: 1.3rem;
  line-height: 1;
}

.rf-celebration-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.01em;
}

.rf-celebration-people {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 14px 8px;
  background: #fff;
}

.rf-celebration-person {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rf-celebration-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e5e7eb;
  flex-shrink: 0;
}

.rf-celebration-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #111827;
}

.rf-celebration-message {
  font-size: 0.82rem;
  color: #6b7280;
  line-height: 1.55;
  margin: 0;
  padding: 0 14px 12px;
  background: #fff;
}
</style>